import { MAX_INTEGER } from "@/constants/common";
import { DATE_FORMAT } from "@/constants/datetime";
import { ECompanyContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { validator } from "@/utils/validator";

export const companySchema = validator.object().shape({
  companyName: validator.string().trim().emptyToNull().max(60).label("企業名").required(),
  contractStatus: validator
    .string()
    .isSelection()
    .emptyToNull()
    .label("ステータス")
    .test((value, ctx) => {
      const todayStr = dayjs().format(DATE_FORMAT);
      const trialEndDateStr = ctx.parent.trialEndDate ? dayjs(ctx.parent.trialEndDate).format(DATE_FORMAT) : null;
      const contractEndDateStr = ctx.parent.contractEndDate ? dayjs(ctx.parent.contractEndDate).format(DATE_FORMAT) : null;
      const contractStartDateStr = ctx.parent.contractStartDate ? dayjs(ctx.parent.contractStartDate).format(DATE_FORMAT) : null;
      if (
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === ECompanyContractStatus.TRIAL &&
        trialEndDateStr &&
        dayjs(todayStr).isAfter(dayjs(trialEndDateStr))
      ) {
        return ctx.createError({ message: "トライアル終了日が過ぎているため、ステータスを「トライアル」に設定することはできません。" });
      }
      if (
        ctx.parent.contractStatus &&
        [ECompanyContractStatus.TRIAL, ECompanyContractStatus.DURING_CONTRACT].includes(ctx.parent.contractStatus) &&
        contractEndDateStr &&
        dayjs(todayStr).isAfter(dayjs(contractEndDateStr))
      ) {
        return ctx.createError({ message: "解約日を過ぎているため、ステータスを「トライアル」または「契約中」に設定することはできません。" });
      }
      if (
        !ctx.parent.trialEndDate &&
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === ECompanyContractStatus.TRIAL &&
        contractStartDateStr &&
        dayjs(todayStr).isSameOrAfter(contractStartDateStr)
      ) {
        return ctx.createError({ message: "契約開始日が本日または過去の日付の場合、「トライアル」ステータスは選択できません。" });
      }
      if (
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === ECompanyContractStatus.TRIAL_CANCELLED &&
        contractStartDateStr &&
        dayjs(contractStartDateStr).isSameOrBefore(dayjs(todayStr))
      ) {
        return ctx.createError({ message: "契約開始日が本日または過去の日付の場合、「トライアル解約済み」ステータスは選択できません。" });
      }
      return true;
    })
    .required(),
  specificIndustryId: validator.string().isSelection().emptyToNull().label("特定産業分野").required(),
  companyDescription: validator.string().trim().emptyToNull().label("企業情報").notRequired(),
  trialStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("トライアル開始日")
    .isBeforeDate("trialEndDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .notRequired(),
  trialEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .isSameOrBeforeDate("contractStartDate", { startLabel: "トライアル終了日", endLabel: "契約開始日" })
    .label("トライアル終了日")
    .notRequired(),
  contractStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("契約開始日")
    .isSameOrAfterDate("trialEndDate", { startLabel: "トライアル終了日", endLabel: "契約開始日" })
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "契約開始日" })
    .when("contractStatus", {
      is: ECompanyContractStatus.DURING_CONTRACT,
      then: (schema) => schema.required(),
    })
    .notRequired(),
  contractEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("解約日")
    .notRequired()
    .test((value, ctx) => {
      const trialEndDateStr = ctx.parent.trialEndDate ? dayjs(ctx.parent.trialEndDate).format(DATE_FORMAT) : null;
      const contractEndDateStr = ctx.parent.contractEndDate ? dayjs(ctx.parent.contractEndDate).format(DATE_FORMAT) : null;
      const contractStartDateStr = ctx.parent.contractStartDate ? dayjs(ctx.parent.contractStartDate).format(DATE_FORMAT) : null;
      if (contractEndDateStr && contractStartDateStr && dayjs(contractStartDateStr).isSameOrAfter(dayjs(contractEndDateStr))) {
        return ctx.createError({ message: "「解約日」は「契約開始日」より後の日付を指定してください。" });
      }
      if (contractEndDateStr && trialEndDateStr && dayjs(trialEndDateStr).isSameOrAfter(dayjs(contractEndDateStr))) {
        return ctx.createError({ message: "「解約日」は「トライアル終了日」より後の日付を指定してください。" });
      }
      return true;
    }),
  startReportYear: validator.mixed().isSelection().isDayjs().label("定期届出の開始年度").required(),
  startReportQuarter: validator.number().isSelection().emptyToNull().label("定期届出の開始四半期").limit(MAX_INTEGER).required(),
  pricePerEmployee: validator.number().decimal().emptyToNull().positive().integer().label("単価設定").limit(MAX_INTEGER).notRequired(),
  note: validator.string().trim().emptyToNull().label("備考").notRequired(),
});
